/**
 * ShopPostShareCard.tsx — Canvas share card for a shop post.
 * Redraws the whole post (shop header, photos, title, body, stats) as a
 * single image, then shares it through the phone's native share sheet so it
 * can be reposted to Instagram, WhatsApp, Messages, and anywhere else.
 */

import { useEffect, useRef, useState } from 'react'
import { X, Download, Share2 } from 'lucide-react'
import { supabaseUrl, supabaseAnonKey } from '../../lib/supabase'

interface ShopPost {
  id: string
  title: string
  body: string
  photo_url: string | null
  photo_urls: string[] | null
  category: string
  created_at: string
  approved_at: string | null
  coffee_shops: {
    id: string
    name: string
    city: string | null
    state: string | null
    photo_url: string | null
  } | null
}

interface Props {
  post: ShopPost
  likeCount: number
  commentCount: number
  onClose: () => void
}

const APP_URL = 'https://socialbrewapp.com'
const SCALE = 2

const CATEGORY_LABELS: Record<string, string> = {
  update: 'Update',
  event: 'Event',
  menu: 'New menu item',
  special: 'Special',
}
const CATEGORY_COLORS: Record<string, { bg: string; text: string }> = {
  update:  { bg: '#e8f4fd', text: '#0369a1' },
  event:   { bg: '#f0fdf4', text: '#166534' },
  menu:    { bg: '#fdf0dc', text: '#c8853a' },
  special: { bg: '#fef9c3', text: '#854d0e' },
}

function timeAgo(d: string) {
  const m = Math.floor((Date.now() - new Date(d).getTime()) / 60000)
  if (m < 1) return 'Just now'
  if (m < 60) return `${m}m ago`
  if (m < 1440) return `${Math.floor(m / 60)}h ago`
  return `${Math.floor(m / 1440)}d ago`
}

async function loadImage(url: string): Promise<HTMLImageElement | null> {
  const attempts = [
    `${supabaseUrl}/functions/v1/image-proxy?url=${encodeURIComponent(url)}&apikey=${supabaseAnonKey}`,
    url,
  ]
  for (const src of attempts) {
    try {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve()
        img.onerror = () => reject()
        img.src = src
        setTimeout(() => reject(), 5000)
      })
      if (img.naturalWidth > 0) return img
    } catch { /* try next */ }
  }
  return null
}

function loadLocalImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise(resolve => {
    const img = new Image()
    img.onload = () => resolve(img.naturalWidth > 0 ? img : null)
    img.onerror = () => resolve(null)
    img.src = src
    setTimeout(() => resolve(null), 4000)
  })
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number
) {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.lineTo(x + w - r, y)
  ctx.arcTo(x + w, y, x + w, y + r, r)
  ctx.lineTo(x + w, y + h - r)
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r)
  ctx.lineTo(x + r, y + h)
  ctx.arcTo(x, y + h, x, y + h - r, r)
  ctx.lineTo(x, y + r)
  ctx.arcTo(x, y, x + r, y, r)
  ctx.closePath()
}

function wrapText(
  ctx: CanvasRenderingContext2D, text: string, maxWidth: number
): string[] {
  const lines: string[] = []
  // Preserve author line breaks, then wrap each paragraph to the width
  for (const paragraph of text.split('\n')) {
    if (paragraph.trim() === '') { lines.push(''); continue }
    const words = paragraph.split(' ')
    let current = ''
    for (const word of words) {
      const test = current ? `${current} ${word}` : word
      if (ctx.measureText(test).width > maxWidth && current) {
        lines.push(current)
        current = word
      } else {
        current = test
      }
    }
    if (current) lines.push(current)
  }
  return lines
}

// Heart outline — matches the feed's like icon
function drawHeart(ctx: CanvasRenderingContext2D, cx: number, cy: number, size: number, color: string) {
  const s = size / 24
  ctx.save()
  ctx.translate(cx - 12 * s, cy - 12 * s)
  ctx.scale(s, s)
  ctx.beginPath()
  ctx.moveTo(12, 21)
  ctx.bezierCurveTo(12, 21, 3, 15, 3, 8.5)
  ctx.bezierCurveTo(3, 5.4, 5.4, 3, 8.5, 3)
  ctx.bezierCurveTo(10.3, 3, 12, 4.2, 12, 4.2)
  ctx.bezierCurveTo(12, 4.2, 13.7, 3, 15.5, 3)
  ctx.bezierCurveTo(18.6, 3, 21, 5.4, 21, 8.5)
  ctx.bezierCurveTo(21, 15, 12, 21, 12, 21)
  ctx.closePath()
  ctx.strokeStyle = color
  ctx.lineWidth = 2
  ctx.lineJoin = 'round'
  ctx.stroke()
  ctx.restore()
}

// Comment bubble outline — matches the feed's comment icon
function drawComment(ctx: CanvasRenderingContext2D, cx: number, cy: number, size: number, color: string) {
  const s = size / 24
  ctx.save()
  ctx.translate(cx - 12 * s, cy - 12 * s)
  ctx.scale(s, s)
  ctx.beginPath()
  ctx.moveTo(21, 11.5)
  ctx.bezierCurveTo(21, 15.9, 16.9, 19.5, 12, 19.5)
  ctx.bezierCurveTo(10.9, 19.5, 9.9, 19.3, 8.9, 19)
  ctx.lineTo(3, 20.5)
  ctx.lineTo(4.6, 15.2)
  ctx.bezierCurveTo(3.6, 14.1, 3, 12.9, 3, 11.5)
  ctx.bezierCurveTo(3, 7.1, 7.1, 3.5, 12, 3.5)
  ctx.bezierCurveTo(16.9, 3.5, 21, 7.1, 21, 11.5)
  ctx.closePath()
  ctx.strokeStyle = color
  ctx.lineWidth = 2
  ctx.lineJoin = 'round'
  ctx.stroke()
  ctx.restore()
}

export default function ShopPostShareCard({ post, likeCount, commentCount, onClose }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [generating, setGenerating] = useState(true)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [blob, setBlob] = useState<Blob | null>(null)
  const [sharing, setSharing] = useState(false)
  const [status, setStatus] = useState<string | null>(null)

  const shop = post.coffee_shops
  const photos = (
    (post.photo_urls && post.photo_urls.length > 0)
      ? post.photo_urls
      : post.photo_url ? [post.photo_url] : []
  ).filter(Boolean).slice(0, 4) as string[]
  const location = [shop?.city, shop?.state].filter(Boolean).join(', ')
  const catLabel = CATEGORY_LABELS[post.category] || post.category
  const catStyle = CATEGORY_COLORS[post.category] || { bg: '#f5f5f5', text: '#555' }

  useEffect(() => { generateCard() }, [])

  async function generateCard() {
    setGenerating(true)
    setStatus(null)
    const canvas = canvasRef.current
    if (!canvas) { setGenerating(false); return }

    const W = 390
    const PAD = 20
    const CARD_MX = 16
    const CARD_W = W - CARD_MX * 2
    const CARD_R = 20
    const HEADER_H = 76
    const CARD_PAD_TOP = 16
    const SHOP_ROW_H = 52
    const CARD_PAD_BOT = 16
    const FOOTER_H = 68

    // Load logo, shop avatar and photos in parallel
    const [logoImg, shopImg, ...photoImgs] = await Promise.all([
      loadLocalImage('/icon-192.png'),
      shop?.photo_url ? loadImage(shop.photo_url) : Promise.resolve(null),
      ...photos.map(u => loadImage(u)),
    ])
    const loadedPhotos = photoImgs.filter(Boolean) as HTMLImageElement[]
    const PHOTO_H = loadedPhotos.length > 0 ? 260 : 0

    // Pre-measure title + body
    const tmpCtx = document.createElement('canvas').getContext('2d')!
    tmpCtx.font = `bold 17px system-ui, -apple-system, sans-serif`
    const titleLines = post.title ? wrapText(tmpCtx, post.title, CARD_W - PAD * 2) : []
    const TITLE_H = titleLines.length > 0 ? titleLines.length * 23 + 8 : 0

    tmpCtx.font = `14px system-ui, -apple-system, sans-serif`
    const bodyLines = post.body ? wrapText(tmpCtx, post.body, CARD_W - PAD * 2) : []
    const maxBodyLines = 8
    const shownBody = bodyLines.slice(0, maxBodyLines)
    const BODY_H = shownBody.length > 0 ? shownBody.length * 21 + 10 : 0

    const LOC_H = location ? 24 : 0
    const ACTIONS_H = 40

    const CARD_H =
      CARD_PAD_TOP + SHOP_ROW_H + PHOTO_H + 12 + TITLE_H + BODY_H +
      LOC_H + ACTIONS_H + CARD_PAD_BOT

    const TOTAL_H = HEADER_H + 16 + CARD_H + 16 + FOOTER_H

    canvas.width = W * SCALE
    canvas.height = TOTAL_H * SCALE
    const ctx = canvas.getContext('2d')!
    ctx.scale(SCALE, SCALE)
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'

    const fitText = (text: string, maxW: number): string => {
      if (ctx.measureText(text).width <= maxW) return text
      let t = text
      while (t.length > 1 && ctx.measureText(t + '…').width > maxW) t = t.slice(0, -1)
      return t + '…'
    }

    // Background
    const bg = ctx.createLinearGradient(0, 0, 0, TOTAL_H)
    bg.addColorStop(0, '#fdfaf5')
    bg.addColorStop(1, '#faf6ef')
    ctx.fillStyle = bg
    ctx.fillRect(0, 0, W, TOTAL_H)

    // ── Header ────────────────────────────────────────────────
    let Y = 0
    ctx.fillStyle = '#fdfaf5'
    ctx.fillRect(0, Y, W, HEADER_H)
    ctx.strokeStyle = '#e8d8c0'
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(0, Y + HEADER_H)
    ctx.lineTo(W, Y + HEADER_H)
    ctx.stroke()

    const iconCX = PAD + 22, iconCY = Y + HEADER_H / 2
    const LOGO = 44, logoX = iconCX - LOGO / 2, logoY = iconCY - LOGO / 2
    const LOGO_R = 12
    if (logoImg) {
      ctx.save()
      roundRect(ctx, logoX, logoY, LOGO, LOGO, LOGO_R)
      ctx.clip()
      ctx.drawImage(logoImg, logoX, logoY, LOGO, LOGO)
      ctx.restore()
    } else {
      roundRect(ctx, logoX, logoY, LOGO, LOGO, LOGO_R)
      ctx.fillStyle = '#1c1008'
      ctx.fill()
      ctx.font = '22px system-ui'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillStyle = '#c8a068'
      ctx.fillText('☕', iconCX, iconCY + 1)
      ctx.textBaseline = 'alphabetic'
      ctx.textAlign = 'left'
    }
    roundRect(ctx, logoX, logoY, LOGO, LOGO, LOGO_R)
    ctx.strokeStyle = '#e8d8c0'
    ctx.lineWidth = 1
    ctx.stroke()

    ctx.font = 'bold 21px Georgia, serif'
    ctx.fillStyle = '#1c0a02'
    ctx.fillText('Social Brew', PAD + 54, Y + HEADER_H / 2 - 2)
    ctx.font = '12px system-ui, -apple-system, sans-serif'
    ctx.fillStyle = '#9b7a55'
    ctx.fillText('Independent coffee only', PAD + 54, Y + HEADER_H / 2 + 17)

    Y += HEADER_H + 16

    // ── Card ──────────────────────────────────────────────────
    ctx.save()
    ctx.shadowColor = 'rgba(28,10,2,0.12)'
    ctx.shadowBlur = 20
    ctx.shadowOffsetY = 6
    roundRect(ctx, CARD_MX, Y, CARD_W, CARD_H, CARD_R)
    ctx.fillStyle = 'white'
    ctx.fill()
    ctx.restore()

    const CX = CARD_MX
    let CY = Y + CARD_PAD_TOP

    // Shop row: avatar + name + category pill + time
    const AV_R = 18
    const avCX = CX + PAD + AV_R
    const avCY = CY + AV_R
    ctx.save()
    ctx.beginPath()
    ctx.arc(avCX, avCY, AV_R, 0, Math.PI * 2)
    ctx.clip()
    if (shopImg) {
      const ratio = Math.max((AV_R * 2) / shopImg.naturalWidth, (AV_R * 2) / shopImg.naturalHeight)
      const dw = shopImg.naturalWidth * ratio
      const dh = shopImg.naturalHeight * ratio
      ctx.drawImage(shopImg, avCX - dw / 2, avCY - dh / 2, dw, dh)
    } else {
      ctx.fillStyle = '#f5e6d0'
      ctx.fillRect(avCX - AV_R, avCY - AV_R, AV_R * 2, AV_R * 2)
      ctx.font = '18px system-ui'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText('☕', avCX, avCY + 1)
      ctx.textAlign = 'left'
      ctx.textBaseline = 'alphabetic'
    }
    ctx.restore()
    ctx.beginPath()
    ctx.arc(avCX, avCY, AV_R, 0, Math.PI * 2)
    ctx.strokeStyle = '#e8d4b0'
    ctx.lineWidth = 1.5
    ctx.stroke()

    const textX = CX + PAD + AV_R * 2 + 10
    ctx.font = `bold 14px system-ui, -apple-system, sans-serif`
    ctx.fillStyle = '#1c0a02'
    ctx.fillText(fitText(shop?.name || 'Coffee shop', CARD_W - (textX - CX) - PAD), textX, CY + 14)

    // Category pill + time (second line)
    let metaX = textX
    ctx.font = `10px system-ui, -apple-system, sans-serif`
    const pillLabel = catLabel
    const pillW = ctx.measureText(pillLabel).width + 14
    roundRect(ctx, metaX, CY + 22, pillW, 16, 8)
    ctx.fillStyle = catStyle.bg
    ctx.fill()
    ctx.fillStyle = catStyle.text
    ctx.fillText(pillLabel, metaX + 7, CY + 33)
    metaX += pillW + 8

    ctx.font = `11px system-ui, -apple-system, sans-serif`
    ctx.fillStyle = '#9b7a55'
    ctx.fillText(timeAgo(post.approved_at || post.created_at), metaX, CY + 33)

    CY += SHOP_ROW_H

    // Photos — single shown in full (contain), 2–4 in a quadrant grid (cover)
    if (loadedPhotos.length > 0 && PHOTO_H > 0) {
      const GAP = 2

      function drawCover(img: HTMLImageElement, px: number, py: number, pw: number, ph: number) {
        const ratio = Math.max(pw / img.naturalWidth, ph / img.naturalHeight)
        const dw = img.naturalWidth * ratio
        const dh = img.naturalHeight * ratio
        ctx.save()
        ctx.beginPath()
        ctx.rect(px, py, pw, ph)
        ctx.clip()
        ctx.drawImage(img, px + (pw - dw) / 2, py + (ph - dh) / 2, dw, dh)
        ctx.restore()
      }

      function drawContain(img: HTMLImageElement, px: number, py: number, pw: number, ph: number) {
        const ratio = Math.min(pw / img.naturalWidth, ph / img.naturalHeight)
        const dw = img.naturalWidth * ratio
        const dh = img.naturalHeight * ratio
        ctx.save()
        ctx.beginPath()
        ctx.rect(px, py, pw, ph)
        ctx.clip()
        ctx.fillStyle = '#0a0605'
        ctx.fillRect(px, py, pw, ph)
        ctx.drawImage(img, px + (pw - dw) / 2, py + (ph - dh) / 2, dw, dh)
        ctx.restore()
      }

      // Rounded clip around the whole photo block
      ctx.save()
      roundRect(ctx, CX + PAD, CY, CARD_W - PAD * 2, PHOTO_H, 14)
      ctx.clip()

      const bx = CX + PAD
      const bw = CARD_W - PAD * 2

      if (loadedPhotos.length === 1) {
        drawContain(loadedPhotos[0], bx, CY, bw, PHOTO_H)
      } else if (loadedPhotos.length === 2) {
        const colW = (bw - GAP) / 2
        drawCover(loadedPhotos[0], bx, CY, colW, PHOTO_H)
        drawCover(loadedPhotos[1], bx + colW + GAP, CY, colW, PHOTO_H)
      } else if (loadedPhotos.length === 3) {
        const topH = Math.floor(PHOTO_H * 0.56)
        const botH = PHOTO_H - topH - GAP
        const colW = (bw - GAP) / 2
        drawCover(loadedPhotos[0], bx, CY, bw, topH)
        drawCover(loadedPhotos[1], bx, CY + topH + GAP, colW, botH)
        drawCover(loadedPhotos[2], bx + colW + GAP, CY + topH + GAP, colW, botH)
      } else {
        const colW = (bw - GAP) / 2
        const rowH = (PHOTO_H - GAP) / 2
        drawCover(loadedPhotos[0], bx, CY, colW, rowH)
        drawCover(loadedPhotos[1], bx + colW + GAP, CY, colW, rowH)
        drawCover(loadedPhotos[2], bx, CY + rowH + GAP, colW, rowH)
        drawCover(loadedPhotos[3], bx + colW + GAP, CY + rowH + GAP, colW, rowH)
      }
      ctx.restore()
      CY += PHOTO_H + 12
    }

    // Title
    if (titleLines.length > 0) {
      ctx.font = `bold 17px system-ui, -apple-system, sans-serif`
      ctx.fillStyle = '#1c0a02'
      titleLines.forEach((line, i) => {
        ctx.fillText(line, CX + PAD, CY + 17 + i * 23)
      })
      CY += TITLE_H
    }

    // Body
    if (shownBody.length > 0) {
      ctx.font = `14px system-ui, -apple-system, sans-serif`
      ctx.fillStyle = '#5b4636'
      shownBody.forEach((line, i) => {
        const isLast = i === shownBody.length - 1
        const text = (isLast && bodyLines.length > maxBodyLines)
          ? fitText(line + ' …', CARD_W - PAD * 2)
          : line
        ctx.fillText(text, CX + PAD, CY + 15 + i * 21)
      })
      CY += BODY_H
    }

    // Location
    if (location) {
      ctx.font = `11px system-ui, -apple-system, sans-serif`
      ctx.fillStyle = '#9b7a55'
      ctx.fillText(`📍 ${location}`, CX + PAD, CY + 14)
      CY += LOC_H
    }

    // Actions — like + comment counts
    {
      const iconY = CY + 20
      ctx.textBaseline = 'middle'
      let ax = CX + PAD + 9
      drawHeart(ctx, ax, iconY, 19, '#9b7a45')
      ax += 14
      ctx.font = `bold 13px system-ui, -apple-system, sans-serif`
      ctx.fillStyle = '#9b7a45'
      ctx.fillText(String(likeCount), ax, iconY + 1)
      ax += ctx.measureText(String(likeCount)).width + 18

      drawComment(ctx, ax, iconY, 19, '#7a5c3a')
      ax += 14
      ctx.font = `13px system-ui, -apple-system, sans-serif`
      ctx.fillStyle = '#7a5c3a'
      ctx.fillText(String(commentCount), ax, iconY + 1)
      ctx.textBaseline = 'alphabetic'
      CY += ACTIONS_H
    }

    // ── Footer ────────────────────────────────────────────────
    const footerY = Y + CARD_H + 16
    ctx.font = `12px system-ui, -apple-system, sans-serif`
    ctx.fillStyle = '#9b7a55'
    ctx.textAlign = 'center'
    ctx.fillText('Discover independent coffee shops', W / 2, footerY + 20)
    ctx.font = `bold 13px system-ui, -apple-system, sans-serif`
    ctx.fillStyle = '#c8853a'
    ctx.fillText(`Join Social Brew → ${APP_URL}`, W / 2, footerY + 42)
    ctx.textAlign = 'left'

    canvas.toBlob(b => {
      if (b) {
        setBlob(b)
        setPreviewUrl(prev => {
          if (prev) URL.revokeObjectURL(prev)
          return URL.createObjectURL(b)
        })
      } else {
        setStatus('Could not generate card — try again')
      }
      setGenerating(false)
    }, 'image/png', 0.95)
  }

  async function share() {
    if (!blob) return
    setSharing(true)
    setStatus(null)
    const file = new File([blob], 'social-brew.png', { type: 'image/png' })

    // Native share sheet with the card image — offers every app that
    // accepts images (Instagram, WhatsApp, Messages, X, email, …).
    if (navigator.share && navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({
          files: [file],
          title: `${shop?.name || 'A coffee shop'} on Social Brew`,
          text: `${post.title}\n${APP_URL}`,
        })
        setSharing(false); return
      } catch (e: any) {
        if (e?.name === 'AbortError') { setSharing(false); return }
      }
    }

    // Fallback — save the image so it can be posted manually
    download()
    setStatus('Image saved — open your favorite app and post it from your gallery')
    setSharing(false)
  }

  function download() {
    if (!previewUrl) return
    const a = document.createElement('a')
    a.href = previewUrl
    a.download = `social-brew-${(shop?.name || 'post').replace(/\s+/g, '-').toLowerCase()}.png`
    a.click()
  }

  return (
    <div className="fixed inset-0 z-[300] flex flex-col items-center justify-end"
      style={{ background: 'rgba(0,0,0,0.92)' }} onClick={onClose}>
      <div className="w-full max-w-sm bg-white rounded-t-3xl pb-8"
        onClick={e => e.stopPropagation()}>

        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <h3 className="font-bold text-coffee-800 text-base">Share this post</h3>
          <button onClick={onClose} aria-label="Close"
            className="w-8 h-8 rounded-full bg-cream-100 flex items-center justify-center">
            <X size={15} className="text-coffee-500" />
          </button>
        </div>

        {/* Preview */}
        <div className="mx-5 mb-4 rounded-2xl overflow-hidden border border-cream-200 bg-cream-50"
          style={{ height: 300 }}>
          {generating ? (
            <div className="w-full h-full flex flex-col items-center justify-center gap-2">
              <div className="w-6 h-6 rounded-full border-2 border-caramel border-t-transparent animate-spin" />
              <p className="text-coffee-400 text-xs">Building your card...</p>
            </div>
          ) : previewUrl ? (
            <img src={previewUrl} alt="Preview" className="w-full h-full object-contain" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <p className="text-coffee-300 text-xs text-center px-4">{status || 'Preview unavailable'}</p>
            </div>
          )}
        </div>

        {status && previewUrl && (
          <p className="mx-5 mb-3 text-coffee-500 text-xs text-center leading-relaxed bg-cream-50 rounded-xl px-3 py-2">
            {status}
          </p>
        )}

        <div className="px-5 flex flex-col gap-2">
          <button onClick={share} disabled={generating || sharing || !blob}
            className="w-full py-3.5 rounded-2xl text-white font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-40"
            style={{ background: 'linear-gradient(135deg, #c8853a, #9b5e1a)' }}>
            <Share2 size={17} />
            {sharing ? 'Opening...' : 'Share'}
          </button>
          <button onClick={download} disabled={generating || !blob}
            className="w-full py-3 rounded-2xl bg-cream-100 text-coffee-700 font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-40">
            <Download size={15} />
            Save image instead
          </button>
        </div>

        <p className="px-5 pt-3 text-coffee-300 text-xs text-center">
          Choose Instagram, WhatsApp, Messages, or anywhere else ☕
        </p>

        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </div>
    </div>
  )
}
